/**
 * Socket Event Names - Single Source of Truth
 * 
 * All socket event names used in the frontend application should be defined here.
 * This ensures consistency with the backend and makes it easier to refactor event names.
 * 
 * NOTE: These constants should match the backend constants in:
 * messaging-gateway/src/infrastructure/socket/socket-events.ts
 * 
 * Usage:
 *   import { SocketEvents } from '../constants/socket-events';
 *   useSocketEvent(SocketEvents.MESSAGE, handler);
 */

export const SocketEvents = {
  // ==================== Client → Server Events ====================
  // Events that clients send to the server
  JOIN_TENANT: 'joinTenant',
  LEAVE_TENANT: 'leaveTenant', // Drop a tenant subscription (workspace left / context dropped)
  SEND_MESSAGE: 'sendMessage',
  JOIN_CONVERSATION: 'joinConversation',
  LEAVE_CONVERSATION: 'leaveConversation',
  JOIN_PAGE: 'joinPage',
  LEAVE_PAGE: 'leavePage',
  TOKEN_UPDATE: 'token:update',              // Token update (when token is refreshed)

  // ==================== Server → Client Events ====================
  // Events that the server emits to clients
  
  // Message Events
  MESSAGE: 'event:message',              // New message received
  STATUS: 'event:status',                // Message status change
  TYPING: 'event:typing',                // Typing indicator
  READ: 'event:read',                    // Read receipt
  DELIVERY: 'event:delivery',            // Delivery confirmation
  REACTION: 'event:reaction',            // Message reaction

  // Sender/User List Events
  SENDER_LIST_CHANGED: 'sender:list:changed',  // Active sender list updated

  // Conversation Inbox Events (operator-thread lifecycle)
  // CONVERSATION_BUMP: tiny payload, used to freshen inbox list entries
  // without re-receiving full message payloads. Emitted to pageRoom only.
  // Payload: { conversationId, pageId, psid, channel, lastMessageAt, lastSenderType }
  CONVERSATION_BUMP: 'conversation:bump',
  // CONVERSATION_REOPENED: a CLOSED conversation transitioned back to OPEN
  // (auto-grace customer reply or operator-initiated reopen). Emitted to
  // pageRoom + conversationRoom BEFORE the next MESSAGE event lands.
  // Payload: { conversationId, pageId, psid, channel, reopenedAt, reopenedBy, previousReason?, closedAt?, closedBy? }
  CONVERSATION_REOPENED: 'conversation:reopened',
  // Assignment lifecycle — another operator (or the system) changed who holds a
  // conversation. Page + conversation + tenant rooms; the row moves between
  // Unassigned / Mine / bot / closed views. See lib/lifecycle.ts.
  // CLAIMED payload: { conversationId, pageId, psid, channel, assignedOperator, assignedOperatorName, claimedAt,
  //   assignedBy?, previousOperator? } — the last two when an admin gave it to someone (POST :id/assign).
  CONVERSATION_CLAIMED: 'conversation:claimed',
  // Payload: { conversationId, pageId, psid, channel, releasedBy, releasedAt, previousOperator }
  CONVERSATION_RELEASED: 'conversation:released',
  // Payload: { conversationId, pageId, psid, channel, handedBackBy, handedBackAt }
  CONVERSATION_HANDED_BACK: 'conversation:handed_back',
  // Payload: { conversationId, pageId, psid, channel, closedAt?, closedBy, closedReason }
  CONVERSATION_CLOSED: 'conversation:closed',
  // CONVERSATION_HANDLER_CHANGED: who is answering changed OUTSIDE the gateway
  // — a `cloud_control` event, today only from chatbot_mn. `handler:'operator'`
  // means a human is needed (usually the customer asked for one) with nobody
  // assigned yet, so Unassigned must gain the row; `handler:'ai'` means the bot
  // took back over (e.g. the 30-min idle auto-unmute) and human views must drop
  // it. Emitted to the tenant room, because the operators who need to see a
  // waiting customer are precisely the ones not in that conversation yet.
  // Payload: { conversationId, pageId, psid, channel, handler, assignedOperator, reason, at }
  CONVERSATION_HANDLER_CHANGED: 'conversation:handler:changed',
  // CONVERSATION_OWNERSHIP_CHANGED: Meta thread ownership (Conversation Routing) changed —
  // thread-control spec §5.2. Emitted to the tenant room.
  // Payload: { conversationId, pageId, psid, channel, ownerAppId: string|null, ownedByUs: boolean, ownershipSince: string|null }
  CONVERSATION_OWNERSHIP_CHANGED: 'conversation:ownership:changed',
  // CONVERSATION_AUTOMATION_CHANGED: chatbot v2 paused / resumed its automatic replies to a customer (bot-loop
  // detector). Tenant room, sender-level — patch every row of that pageId + psid.
  // Payload: { conversationId|null, pageId, psid, channel, automation: { paused, until|null, peerBot, reason, episode } | null, at }
  CONVERSATION_AUTOMATION_CHANGED: 'conversation:automation:changed',
  // PAGE_TOKEN_CHANGED: a page's Meta connection (stored page token) expired or works again — token spec B3-4.
  // Tenant room. Display only: the bot cannot answer on an expired page; operators may still send.
  // Payload: { system: 'chatbot'|'chatbot_v2', channel, pageId, state: 'valid'|'expired', since: string|null, reason: string|null }
  PAGE_TOKEN_CHANGED: 'page:token:changed',

  // Operator Events
  OPERATOR_LOGGED_IN: 'operator:loggedIn',     // Operator successfully logged in
  APPEARANCE_CHANGED: 'appearance:changed', // UI look changed (system or this workspace); refetch GET /api/appearance. Gateway: AppearanceService.
  OPERATOR_STATUS_CHANGED: 'operator:statusChanged', // To your own room: { tenantId, status } — switched on another tab, or reset to off after 12 h away. Gateway: OperatorStatusService.
  OPERATOR_ACCESS_CHANGED: 'operator:access:changed', // Operator's tenant membership or allowed pages changed; client should refetch userinfo + /allowed-pages. Emitter side tracked in messaging-gateway/ROADMAP.md (Initiative: live-access-refresh).

  // Room Events
  JOINED: 'joined',                      // Successfully joined a room
  LEFT: 'left',                          // Successfully left a room

  // System Events
  ERROR: 'error',                        // Generic error (deprecated - use specific error events)
  ECHO: 'echo',                          // Echo test event
  NOTIFICATION: 'notification',          // General notification
  
  // Error Events (specific)
  ERROR_VALIDATION: 'error:validation',        // Validation errors (invalid input, missing fields)
  ERROR_AUTH: 'error:auth',                    // Authentication/authorization errors
  ERROR_MESSAGE_SEND: 'error:message:send',   // Message send failures
  ERROR_MESSAGE_STATUS: 'error:message:status', // Message status update errors
  ERROR_SYSTEM: 'error:system',                // System errors (DB, network, etc.)
} as const;

/**
 * Type-safe socket event names
 */
export type SocketEventName = typeof SocketEvents[keyof typeof SocketEvents];

